/**
 * Single source of truth for role hierarchy on the frontend.
 * Mirrors `bloom-auth::token::Role` + `Role::has_at_least` on the BE
 * so that FE-side gating decisions match the extractor semantics in
 * `apps/api/crates/bloom-api/src/middleware/auth.rs`.
 *
 * Roles ascend by privilege:
 *   client (1) < behandelaar (2) < beheerder (3) < superbeheerder (4)
 *
 * `superbeheerder` is the universal escape hatch — passes every gate.
 *
 * FB-8: `client` no longer has a surface on the web — a client who signs in
 * here gets the "use the mobile app" page — but the value stays in the union
 * because staff screens (user lists, invites) still read and label it.
 *
 * Wire/DB values are Dutch (PO 2026-06-09). `client` stays ASCII `client`
 * (cliënt = the Dutch spelling of the same word — the diacritic appears
 * only in the display label via `roleLabel`).
 *
 * "public" is a frontend-only sentinel emitted by App.tsx when the
 * user is unauthenticated (so the auth pages can route). It is NOT a
 * real role in the BE.
 */

export type RoleName = 'client' | 'behandelaar' | 'beheerder' | 'superbeheerder'

const ROLE_RANK: Record<RoleName, number> = {
  client: 1,
  behandelaar: 2,
  beheerder: 3,
  superbeheerder: 4,
}

/** Human-readable Dutch display label for a role wire value. */
const ROLE_LABEL: Record<RoleName, string> = {
  client: 'Cliënt',
  behandelaar: 'Behandelaar',
  beheerder: 'Beheerder',
  superbeheerder: 'Superbeheerder',
}

export function roleLabel(role: string | null | undefined): string {
  if (!role) return ''
  return ROLE_LABEL[role.toLowerCase() as RoleName] ?? role
}

function rank(role: string | null | undefined): number | null {
  if (!role) return null
  const r = role.toLowerCase() as RoleName
  return ROLE_RANK[r] ?? null
}

/**
 * Returns `true` when `role` has at least the privilege of `required`.
 * Mirrors `Role::has_at_least` on the BE — `Dev` passes every gate,
 * `Admin` passes admin+supervisor+client, etc.
 *
 * Examples:
 *   hasAtLeast('beheerder', 'beheerder')        // true
 *   hasAtLeast('superbeheerder', 'beheerder')   // true (universal)
 *   hasAtLeast('behandelaar', 'beheerder')      // false
 *   hasAtLeast(undefined, 'client')             // false (unauth)
 */
export function hasAtLeast(
  role: string | null | undefined,
  required: RoleName
): boolean {
  const r = rank(role)
  const need = ROLE_RANK[required]
  return r !== null && r >= need
}

/**
 * Returns `true` when `role` is Supervisor or higher.
 * Mirrors `SupervisorAuthUser` on the BE.
 */
export function isSupervisorScope(
  role: string | null | undefined
): boolean {
  return hasAtLeast(role, 'behandelaar')
}

/**
 * Returns `true` when `role` is Admin or higher (Admin + Dev).
 * Mirrors `AdminAuthUser` on the BE.
 */
export function isAdminScope(role: string | null | undefined): boolean {
  return hasAtLeast(role, 'beheerder')
}

/**
 * Roles `actor` may hand out — when inviting (CHL-061-009) and, since
 * CHL-067-009, when changing an existing account's role. Mirrors
 * `bloom_auth::invite::may_invite` on the BE: a superbeheerder hands out
 * everything, a beheerder everything below superbeheerder, a behandelaar
 * only clients. Offering more than this in a dropdown just buys a 403.
 */
export function assignableRoles(role: string | null | undefined): RoleName[] {
  switch ((role ?? '').toLowerCase() as RoleName) {
    case 'superbeheerder':
      return ['client', 'behandelaar', 'beheerder', 'superbeheerder']
    case 'beheerder':
      return ['client', 'behandelaar', 'beheerder']
    case 'behandelaar':
      return ['client']
    default:
      return []
  }
}
